const FIELDS = [
  { key: 'session', label: 'Session', type: 'text' },
  { key: 'lastName', label: 'Nom', type: 'text' },
  { key: 'firstName', label: 'Prénom', type: 'text' },
  { key: 'status', label: 'Statut du dossier', type: 'status' },
  { key: 'email', label: 'E-mail', type: 'text' },
  { key: 'progress', label: 'Avancement (%)', type: 'number' },
  { key: 'createdAt', label: 'Date de création', type: 'date' },
  { key: 'updatedAt', label: 'Dernière modification', type: 'date' }
];
const STATUSES = { in_progress: 'En cours', sent: 'Envoyé', incomplete: 'Incomplet', completed: 'Terminé' };
const DEFAULT_VIEW = {
  columns: ['session', 'lastName', 'firstName', 'status'],
  sorts: [{ field: 'lastName', direction: 'asc' }, { field: 'firstName', direction: 'asc' }],
  filters: [], search: ''
};
const OPERATORS = { contains: 'contient', equals: 'est égal à', not_equals: 'est différent de', starts_with: 'commence par', gte: 'est supérieur ou égal à', lte: 'est inférieur ou égal à', empty: 'est vide', not_empty: 'est renseigné' };
const normalizeText = value => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
const fieldByKey = (key, fields = FIELDS) => fields.find(field => field.key === key);
const operatorsFor = (field, fields = FIELDS) => {
  const type = fieldByKey(field, fields)?.type;
  if (type === 'number' || type === 'date') return ['equals', 'not_equals', 'gte', 'lte'];
  if (type === 'status') return ['equals', 'not_equals'];
  return ['contains', 'equals', 'not_equals', 'starts_with', 'empty', 'not_empty'];
};

function normalizeView(input, fields = FIELDS, pruneMissing = false) {
  if (!input || typeof input !== 'object') throw new Error('Vue invalide.');
  if (pruneMissing && Array.isArray(input.columns) && Array.isArray(input.sorts) && Array.isArray(input.filters)) {
    const columns = input.columns.filter(key => fieldByKey(key, fields));
    input = { ...input, columns: columns.length ? columns : [...DEFAULT_VIEW.columns],
      sorts: input.sorts.filter(sort => fieldByKey(sort?.field, fields)),
      filters: input.filters.filter(filter => fieldByKey(filter?.field, fields) && operatorsFor(filter.field, fields).includes(filter.operator)) };
  }
  if (!Array.isArray(input.columns) || !input.columns.length || input.columns.length > fields.length ||
      input.columns.some(key => !fieldByKey(key, fields)) || new Set(input.columns).size !== input.columns.length) {
    throw new Error('Sélectionnez des colonnes valides, sans doublon.');
  }
  if (!Array.isArray(input.sorts) || input.sorts.length > 3 || input.sorts.some(sort =>
    !sort || !fieldByKey(sort.field, fields) || !['asc', 'desc'].includes(sort.direction))) throw new Error('Tri invalide.');
  if (!Array.isArray(input.filters) || input.filters.length > 10) throw new Error('Vous pouvez ajouter au maximum dix filtres.');
  const filters = input.filters.map(filter => {
    if (!filter || !fieldByKey(filter.field, fields) || !operatorsFor(filter.field, fields).includes(filter.operator)) throw new Error('Filtre invalide.');
    const value = String(filter.value ?? '').trim();
    if (value.length > 254) throw new Error('Valeur de filtre trop longue.');
    if (!['empty', 'not_empty'].includes(filter.operator)) {
      if (!value) throw new Error('Renseignez la valeur de chaque filtre.');
      const type = fieldByKey(filter.field, fields).type;
      if (type === 'number' && !Number.isFinite(Number(value))) throw new Error('Valeur numérique invalide.');
      if (filter.field === 'progress' && (Number(value) < 0 || Number(value) > 100)) throw new Error('L’avancement doit être compris entre 0 et 100.');
      if (type === 'date' && (!/^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isFinite(Date.parse(value)) || new Date(value).toISOString().slice(0, 10) !== value)) throw new Error('Date de filtre invalide.');
      if (type === 'status' && !Object.prototype.hasOwnProperty.call(STATUSES, value)) throw new Error('Statut de filtre invalide.');
    }
    return { field: filter.field, operator: filter.operator, value };
  });
  return { columns: [...input.columns], sorts: input.sorts.map(sort => ({ field: sort.field, direction: sort.direction })), filters, search: String(input.search || '').slice(0, 254) };
}

function sessionLabel(year, semester) {
  return `${year}–${Number(year) + 1} · ${semester === 'autumn' ? 'Automne' : 'Printemps'}`;
}

function dateKey(value) {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  if (!Number.isFinite(Number(value))) return '';
  const parts = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(Number(value)));
  const get = key => parts.find(part => part.type === key)?.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

function applyView(rows, view, fields = FIELDS) {
  view = normalizeView(view, fields, true);
  const query = normalizeText(view.search);
  const result = rows.filter(row => {
    if (query && ![row.lastName, row.firstName, row.email].some(value => normalizeText(value).includes(query))) return false;
    return view.filters.every(filter => {
      const type = fieldByKey(filter.field, fields).type;
      let actual = row[filter.field];
      let expected = filter.value;
      if ((type === 'number' || type === 'date') && (actual === '' || actual === undefined || actual === null)) return filter.operator === 'not_equals';
      if (type === 'number') { actual = Number(actual); expected = Number(expected); }
      else if (type === 'date') { actual = dateKey(actual); }
      else { actual = normalizeText(type === 'status' ? actual : displayValue(row, filter.field, fields)); expected = normalizeText(expected); }
      switch (filter.operator) {
        case 'equals': return actual === expected;
        case 'not_equals': return actual !== expected;
        case 'contains': return actual.includes(expected);
        case 'starts_with': return actual.startsWith(expected);
        case 'gte': return actual >= expected;
        case 'lte': return actual <= expected;
        case 'empty': return !actual;
        case 'not_empty': return Boolean(actual);
        default: return false;
      }
    });
  });
  const collator = new Intl.Collator('fr', { sensitivity: 'base', numeric: true });
  return result.sort((left, right) => {
    for (const sort of view.sorts) {
      const type = fieldByKey(sort.field, fields).type;
      const a = sort.field === 'status' ? STATUSES[left.status] : type === 'date' ? dateKey(left[sort.field]) : type === 'number' ? Number(left[sort.field]) : displayValue(left, sort.field, fields);
      const b = sort.field === 'status' ? STATUSES[right.status] : type === 'date' ? dateKey(right[sort.field]) : type === 'number' ? Number(right[sort.field]) : displayValue(right, sort.field, fields);
      const comparison = type === 'number' ? a - b : collator.compare(String(a ?? ''), String(b ?? ''));
      if (comparison) return sort.direction === 'desc' ? -comparison : comparison;
    }
    return collator.compare(left.id, right.id);
  });
}

function displayValue(row, key, fields = FIELDS) {
  if (key === 'status') return `${STATUSES[row.status] || row.status}${row.status === 'in_progress' ? ` (${row.progress} %)` : ''}`;
  const field = fieldByKey(key, fields);
  const value = row[key];
  if (value === '' || value === undefined || value === null) return '';
  if (field?.type === 'date') {
    const date = dateKey(value);
    return date ? new Intl.DateTimeFormat('fr-FR', { timeZone: 'UTC' }).format(new Date(`${date}T12:00:00Z`)) : '';
  }
  if (field?.question?.type === 'file') return value ? 'Fournie' : 'Non fournie';
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  const option = field?.question?.Options?.find(item => String(item.value) === String(value));
  return option ? option.text || option.label || String(value) : String(value);
}

// The current template is the catalogue; answers never add obsolete questions to it.
function fieldsFromTemplate(model) {
  const fields = [...FIELDS];
  const seen = new Set();
  for (const critere of model.criteres || []) {
    if (Number(critere.id) === 50) continue;
    for (const section of critere.sousCriteres || []) {
      for (const question of section.questions || []) {
        if (['autre', 'paiement'].includes(question.type) || seen.has(String(question.id))) continue;
        seen.add(String(question.id));
        const descriptor = {};
        for (const key of ['id', 'type', 'Options', 'placeholder', 'pattern', 'maxLength', 'maxlength']) {
          if (question[key] !== undefined) descriptor[key] = question[key];
        }
        fields.push({ key: `question:${question.id}`, label: question.etiquette || question.nom || `Question ${question.id}`,
          type: ['date', 'number'].includes(question.type) ? question.type : 'text',
          group: [critere.nom, section.nom].filter(Boolean).join(' · '), question: descriptor });
      }
    }
  }
  return fields;
}

function questionValues(dossier, fields) {
  const answers = new Map();
  for (const entry of dossier?.data || []) for (const critere of entry.criteres || []) {
    if (Number(critere.id) === 50) continue;
    for (const section of critere.sousCriteres || []) for (const question of section.questions || []) {
      answers.set(String(question.id), question);
    }
  }
  return Object.fromEntries(fields.filter(field => field.question).map(field => {
    const answer = answers.get(String(field.question.id));
    const value = field.question.type === 'search' ? answer?.valueName || answer?.value : answer?.value;
    return [field.key, ['string', 'number', 'boolean'].includes(typeof value) ? value : ''];
  }));
}

// Accepte les exports CSV et les cellules copiées de Google Sheets, guillemets inclus.
function parseDelimited(text) {
  if (typeof text !== 'string' || text.length > 1024 * 1024 || new TextEncoder().encode(text).byteLength > 1024 * 1024) throw new Error('Le tableau doit faire moins de 1 Mo.');
  const source = text.replace(/^\uFEFF/, '');
  const counts = { '\t': 0, ';': 0, ',': 0 };
  let inQuotes = false;
  for (let i = 0; i < source.length; i += 1) {
    const c = source[i];
    if (c === '"') { if (inQuotes && source[i + 1] === '"') i += 1; else inQuotes = !inQuotes; }
    else if (!inQuotes && (c === '\n' || c === '\r')) break;
    else if (!inQuotes && Object.prototype.hasOwnProperty.call(counts, c)) counts[c] += 1;
  }
  const delimiter = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
  const rows = [];
  let row = [], value = '', quoted = false, closed = false;
  const pushRow = () => { row.push(value); rows.push(row); row = []; value = ''; closed = false; };
  for (let i = 0; i < source.length; i += 1) {
    const c = source[i];
    if (quoted) {
      if (c === '"' && source[i + 1] === '"') { value += '"'; i += 1; }
      else if (c === '"') { quoted = false; closed = true; }
      else value += c;
    } else if (c === delimiter) { row.push(value); value = ''; closed = false; }
    else if (c === '\r' || c === '\n') { if (c === '\r' && source[i + 1] === '\n') i += 1; pushRow(); }
    else if (c === '"' && !value && !closed) quoted = true;
    else if (closed || c === '"') throw new Error('Le fichier contient des guillemets mal formés.');
    else value += c;
    if (rows.length > 1001) throw new Error('Import limité à 1 000 étudiants à la fois.');
  }
  if (quoted) throw new Error('Le fichier contient une cellule entre guillemets non terminée.');
  if (value || row.length || closed) pushRow();
  return rows;
}

function previewImport(rawRows, existingEmails = []) {
  if (!Array.isArray(rawRows) || rawRows.length > 1001) throw new Error('Import limité à 1 000 étudiants à la fois.');
  const emailKey = value => String(value).trim().normalize('NFC').toLowerCase();
  const seen = new Set(existingEmails.map(emailKey));
  const valid = [], duplicates = [], errors = [];
  rawRows.forEach((row, index) => {
    const line = index + 1;
    if (!Array.isArray(row) || row.some(value => typeof value !== 'string')) { errors.push({ line, message: 'Format de ligne invalide.' }); return; }
    const cells = row.map(value => value.trim());
    if (cells.every(value => !value)) return;
    if (index === 0 && normalizeText(cells[0]) === 'nom' && normalizeText(cells[1]) === 'prenom' && ['mail', 'email', 'e-mail'].includes(normalizeText(cells[2]))) return;
    if (cells.length < 3 || cells.slice(3).some(Boolean)) { errors.push({ line, message: 'Trois colonnes attendues : nom, prénom, e-mail.' }); return; }
    const [lastName, firstName, rawEmail] = cells;
    const email = emailKey(rawEmail);
    if ([lastName, firstName].some(value => !value || value.length > 150 || /[\u0000-\u001F\u007F]/.test(value))) {
      errors.push({ line, message: 'Nom et prénom obligatoires, limités à 150 caractères, sans retour à la ligne.' }); return;
    }
    if (email.length > 254 || !/^[^\s@<>\u0000-\u001F\u007F]+@[^\s@<>\u0000-\u001F\u007F]+\.[^\s@<>\u0000-\u001F\u007F]+$/.test(email)) {
      errors.push({ line, message: 'Adresse e-mail invalide.' }); return;
    }
    if (seen.has(email)) { duplicates.push({ line, email }); return; }
    seen.add(email);
    valid.push({ line, lastName, firstName, email });
  });
  if (valid.length > 1000) throw new Error('Import limité à 1 000 étudiants à la fois.');
  return { valid, duplicates, errors };
}

function exportMatrix(rows, columns, fields = FIELDS) {
  return [columns.map(key => fieldByKey(key, fields).label), ...rows.map(row => columns.map(key => displayValue(row, key, fields)))];
}

function toCsv(matrix) {
  const cell = value => {
    let text = String(value ?? '');
    if (/^[\s\uFEFF]*[=+@-]/.test(text) || /^[\t\r\n]/.test(text)) text = `'${text}`;
    return `"${text.replace(/"/g, '""')}"`;
  };
  return `\uFEFF${matrix.map(row => row.map(cell).join(';')).join('\r\n')}`;
}

module.exports = { fieldsFromTemplate, questionValues, FIELDS, STATUSES, DEFAULT_VIEW, OPERATORS, fieldByKey, operatorsFor, normalizeView, sessionLabel, applyView, displayValue, parseDelimited, previewImport, exportMatrix, toCsv };
